import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  confirmEmployeeApi,
  extendProbationApi,
  fetchBirthAndWorkAnniversaryApi,
  fetchUpcomingConfirmationsApi,
} from "../../api/hrDashboard.api";

interface AnniversaryItem {
  employeeId: string;
  name: string;
  birthday?: { date: string; age: number };
  workAnniversary?: { date: string; yearsOfService: number };
}

interface ConfirmationItem {
  _id: string;
  name: string;
  department?: string;
  location?: string;
  designation: string;
  employmentStatus: string;
  dateOfJoining: string;
  dateOfConfirmation: string;
  probationDays?: number;
  mobile?: string;
  email?: string;
}

interface HrDashboardState {
  loading: boolean;
  anniversaries: AnniversaryItem[];
  confirmations: ConfirmationItem[];
}

const initialState: HrDashboardState = {
  loading: false,
  anniversaries: [],
  confirmations: [],
};

interface FetchAnniversaryParams {
  month: number;
  year: number;
}

// Thunk: Fetch Birthdays & Work Anniversaries
export const fetchBirthAndWorkAnniversary = createAsyncThunk(
  "hrDashboard/fetchBirthAndWorkAnniversary",
  async (params: FetchAnniversaryParams, { rejectWithValue }) => {
    try {
      return await fetchBirthAndWorkAnniversaryApi(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch anniversaries");
    }
  }
);

// Thunk: Fetch Upcoming Confirmations
export const fetchUpcomingConfirmations = createAsyncThunk(
  "hrDashboard/fetchUpcomingConfirmations",
  async (_: void, { rejectWithValue }) => {
    try {
      return await fetchUpcomingConfirmationsApi();
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch confirmations");
    }
  }
);

export const confirmEmployee = createAsyncThunk(
  "hrDashboard/confirmEmployee",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await confirmEmployeeApi(payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to confirm employee");
    }
  }
);

export const extendProbation = createAsyncThunk(
  "hrDashboard/extendProbation",
  async (payload: any, { rejectWithValue }) => {
    try {
      return await extendProbationApi(payload);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to extend probation");
    }
  }
);

const hrDashboardSlice = createSlice({
  name: "hrDashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Anniversaries
      .addCase(fetchBirthAndWorkAnniversary.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBirthAndWorkAnniversary.fulfilled, (state, action) => {
        state.anniversaries = action.payload;
        state.loading = false;
      })
      .addCase(fetchBirthAndWorkAnniversary.rejected, (state) => {
        state.loading = false;
      })

      // Fetch Confirmations
      .addCase(fetchUpcomingConfirmations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUpcomingConfirmations.fulfilled, (state, action) => {
        state.confirmations = action.payload;
        state.loading = false;
      })
      .addCase(fetchUpcomingConfirmations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(confirmEmployee.pending, (state) => {
        state.loading = true;
      })
      .addCase(confirmEmployee.fulfilled, (state, action) => {
        state.confirmations = state.confirmations.filter(
          (c) => c._id !== action.payload._id
        );
        state.loading = false;
      })
      .addCase(confirmEmployee.rejected, (state) => {
        state.loading = false;
      })
      .addCase(extendProbation.pending, (state) => {
        state.loading = true;
      })
      .addCase(extendProbation.fulfilled, (state, action) => {
        const updated = action.payload;
        state.confirmations = state.confirmations.map((c) =>
          c._id === updated._id
            ? { ...c, dateOfConfirmation: updated.dateOfConfirmation }
            : c
        );
        state.loading = false;
      })
      .addCase(extendProbation.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default hrDashboardSlice.reducer;
